import { jsPDF } from "jspdf";
import QRCode from "qrcode";

const formatWhen = (startsAt) => {
  if (!startsAt) return "";
  return new Date(startsAt).toLocaleString("en-NG", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Build a multi-page ticket PDF (one page per ticket) with QR codes.
 * @returns {Promise<Blob>}
 */
export async function buildTicketPdfBlob(order, event = null) {
  const tickets = order?.tickets || [];
  if (!tickets.length) throw new Error("No tickets to export");

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  for (let i = 0; i < tickets.length; i++) {
    if (i > 0) doc.addPage();
    const t = tickets[i];
    const title = event?.title || t.eventTitle || "PendPay Ticket";
    const when = formatWhen(event?.startsAt || t.eventStartsAt);
    const where = event?.location || t.eventLocation || "";
    const tier = t.ticketTypeName || "Ticket";

    // Brand bar
    doc.setFillColor(11, 17, 32);
    doc.rect(0, 0, pageW, 72, "F");
    doc.setTextColor(46, 196, 160);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("PendPay", 40, 44);
    doc.setTextColor(232, 244, 248);
    doc.setFontSize(12);
    doc.text("Event ticket", pageW - 40, 44, { align: "right" });

    doc.setTextColor(11, 17, 32);
    doc.setFontSize(22);
    doc.text(title, 40, 120, { maxWidth: pageW - 80 });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(74, 122, 138);
    let y = 150;
    if (when) {
      doc.text(when, 40, y);
      y += 20;
    }
    if (where) {
      doc.text(where, 40, y);
      y += 20;
    }
    doc.setTextColor(11, 17, 32);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(tier, 40, y + 8);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(74, 122, 138);
    doc.text(`Order ${order.reference || ""}`, 40, y + 28);
    doc.text(`Ticket ${String(t.id || "").slice(0, 8).toUpperCase()}`, 40, y + 46);

    const qrDataUrl = await QRCode.toDataURL(t.qrToken, {
      width: 280,
      margin: 2,
      color: { dark: "#0B1120", light: "#FFFFFF" },
    });
    const qrSize = 220;
    const qrX = (pageW - qrSize) / 2;
    const qrY = Math.min(y + 80, pageH - qrSize - 80);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(qrX - 16, qrY - 16, qrSize + 32, qrSize + 48, 8, 8, "F");
    doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    doc.setTextColor(11, 17, 32);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(tier, pageW / 2, qrY + qrSize + 22, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(74, 122, 138);
    doc.text("Show this QR at the door · PendPay", pageW / 2, pageH - 36, { align: "center" });
  }

  return doc.output("blob");
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function shareTicketPdf(order, event = null) {
  const blob = await buildTicketPdfBlob(order, event);
  const filename = `pendpay-tickets-${order.reference || "order"}.pdf`;
  const file = new File([blob], filename, { type: "application/pdf" });
  const title = event?.title || order.tickets?.[0]?.eventTitle || "PendPay tickets";
  const text = `My PendPay tickets for ${title}`;

  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title, text });
    return { shared: true };
  }
  if (navigator.share) {
    await navigator.share({ title, text });
    downloadBlob(blob, filename);
    return { shared: true, downloaded: true };
  }
  downloadBlob(blob, filename);
  return { shared: false, downloaded: true };
}
